// The map app's state and the rules that change it as a whole: defaults, a link's scope, switching between the
// California and U.S. scopes (docs/architecture-review.md, recommendation 3), opening an area, a level or a data point,
// and what each data point offers in a scope. Pure, so the rules are unit tested (test/web.test.ts); web/main.ts holds
// the live state, builds the next one with these and applies the difference in one place (`go`).
import type { Level, Metric } from "./api.ts";
import type { Screen } from "./screen.ts";
import { COVERAGE, type Coverage } from "../shared/coverage.ts";
import { LEVEL_LABEL } from "../shared/format.ts";
import { BAY_AREA_ID, DEEP_STATE, hasCities, inState } from "../shared/regions.ts";

/** California (the default: every level and layer) or the U.S. (the national data points only, no City level). */
export type Scope = "ca" | "us";

export interface State {
  scope: Scope;
  level: Level;
  metric: string;
  period: string | null; // null = latest
  geo: string | null; // selected area
  freq: "monthly" | "yearly";
  view: "map" | "table" | "accuracy" | "screen" | "compare" | "relate";
  compare: string[]; // areas in the comparison (Compare view)
  y?: string | null; // the second data point (relationship view; the first is `metric`)
  within?: string | null; // the relationship view's places: a state, metro or county; a table's: "bayarea"
  q: string | null; // active question (sidebar), null = browsing all data points
  screen: Screen | null; // active screen (Screener): its matches are marked on the map
  sort?: "asc"; // one-shot from a link: open the table lowest first
}

/** The map opens on California's cities (M12). */
export const DEFAULT_LEVEL: Level = "city";
export const DEFAULT_METRIC = "home_value";
export const defaultState = (): State => ({
  scope: "ca", level: DEFAULT_LEVEL, metric: DEFAULT_METRIC, period: null, geo: null, freq: "yearly", view: "map", q: null, screen: null, compare: [],
});

/** Whether an area id lies in the deep view's state (shared/regions.ts DEEP_STATE); null = can't tell (metros, the U.S.). */
export const inDeepState = (id: string) => inState(DEEP_STATE, id);

/** An area id's level ("us" for the U.S.). */
const levelOf = (id: string) => (id === "us" ? "us" : id.split(":")[0]) as Level;

/** What the rules need to know about data points, questions (from /api/metrics) and cities (from /api/config). */
export interface Facts {
  coverage: (metric: string) => Coverage | undefined;
  questionMetrics: (q: string) => string[] | undefined;
  /** a city's county (`/api/config` cityCounty), null when unknown: known at startup, before the city has loaded */
  cityCounty: (city: string) => string | null;
}

/**
 * The scope a link opens in when it doesn't say: its area's (or, with no area, its first comparison place's: market
 * pages and the onboarding email link to `#/compare?ids=…` alone), else California for a regional data point or a
 * California level (City), the U.S. for the national and state maps, else California (the app's front door).
 */
export function linkScope(h: Partial<State>, f: Facts): Scope {
  if (h.scope) return h.scope;
  const area = h.geo ?? h.compare?.[0];
  const ca = area ? inDeepState(area) : null;
  if (ca != null) return ca ? "ca" : "us";
  if (h.metric && (f.coverage(h.metric) ?? "us") !== "us") return "ca";
  if (h.level === "us" || h.level === "state") return "us";
  return "ca";
}

/**
 * Switch scopes. California → U.S.: City becomes County, a regional data point becomes Home Value, a selected city
 * becomes its county (`f.cityCounty`; a city whose county isn't known is let go) and a question or screen filter that only works in California
 * is dropped. U.S. → California: an area or relationship places outside California are let go, and the national map
 * becomes the state map. Comparison places are kept both ways: Compare is national by design (San Francisco vs. Austin)
 * and is never filtered by scope. Back restores the old scope with all of it, since the scope is in the URL.
 */
export function switchScope(s: State, to: Scope, f: Facts): State {
  if (s.scope === to) return s;
  const next: State = { ...s, scope: to };
  const national = (id: string) => (f.coverage(id) ?? "us") === "us";
  if (to === "us") {
    if (next.level === "city") next.level = "county";
    if (!national(next.metric)) { next.metric = DEFAULT_METRIC; next.period = null; }
    if (next.geo && levelOf(next.geo) === "city") next.geo = f.cityCounty(next.geo);
    if (next.y && !national(next.y)) next.y = null;
    if (next.q && !(f.questionMetrics(next.q) ?? []).includes(next.metric)) next.q = null;
    if (next.screen) {
      const keep = <T extends { metric: string }>(xs: T[]) => xs.filter((x) => national(x.metric));
      const sort = next.screen.sort && f.coverage(next.screen.sort) && !national(next.screen.sort) ? null : next.screen.sort;
      next.screen = { ...next.screen, filters: keep(next.screen.filters), rank: keep(next.screen.rank), sort };
    }
  } else {
    if (next.level === "us") next.level = "state";
    if (next.geo && inDeepState(next.geo) === false) next.geo = null;
    if (next.within && next.within !== BAY_AREA_ID && inDeepState(next.within) === false) next.within = null;
    if (next.screen?.where && inDeepState(next.screen.where.geo_id) === false) next.screen = { ...next.screen, where: null };
  }
  return next;
}

/**
 * A state made valid for its own scope (a link that says scope=us with a California-only data point, or the national map
 * in the California view): the same rules as switching into it.
 */
export function fitScope(s: State, f: Facts): State {
  return s.scope === "us" ? switchScope({ ...s, scope: "ca" }, "us", f) : switchScope({ ...s, scope: "us" }, "ca", f);
}

/**
 * The state a link asks for (`h`: what readHash read from it). A full link spells out the whole state, from the defaults,
 * in the scope it names or belongs in; a bare view link such as "#/accuracy" (`current` given) changes only the view of
 * the current state. Either way the result is fitted to its scope. Startup and Back / Forward both open links this way.
 */
export function stateFromLink(h: Partial<State>, f: Facts, current: State | null = null): State {
  const base: State = current ? { ...current, view: "map" } : defaultState();
  return fitScope({ ...base, ...h, scope: current ? h.scope ?? current.scope : linkScope(h, f) }, f);
}

/**
 * The levels each scope offers: the deep state's run from City (it has cities: CITY_STATES) to State; the U.S. has no
 * City level (cities exist in a few states only) but a national map.
 */
export const levelInScope = (scope: Scope, l: Level) =>
  scope === "us" ? l !== "city" : l !== "us" && (l !== "city" || hasCities(DEEP_STATE.fips));

// ---------- what each data point offers in a scope ----------

/** What the availability rules read about a data point (/api/metrics; `available`: the levels it has data at). */
export type MetricFacts = Pick<Metric, "levels" | "coverage" | "cadence" | "personal"> & { available: Partial<Record<Level, unknown>> };

/** The data points as the rules see them (each with the levels /api/metrics serves), and whether a budget is set. */
export interface Catalog {
  metric: (id: string) => MetricFacts | undefined;
  hasBudget: boolean;
}

/**
 * A data point's levels with data as the views should see them (`served`: what /api/metrics says exists): personal
 * ones only with a budget; in the U.S. view no regional data point (metrics.yaml coverage) and no City level.
 */
export function shownAvailability<T>(m: Pick<MetricFacts, "coverage" | "personal">, served: Partial<Record<Level, T>>, scope: Scope, hasBudget: boolean): Partial<Record<Level, T>> {
  if ((m.personal && !hasBudget) || (scope === "us" && m.coverage !== "us")) return {};
  const shown = { ...served };
  if (scope === "us") delete shown.city;
  return shown;
}

/** Whether a data point (its availability as shown) has data at a level. */
export const hasData = (m: Pick<MetricFacts, "levels" | "available">, l: Level) => m.levels.includes(l) && !!m.available[l];

/** Why a data point can't be picked at a level: no budget, the wrong scope, or no data there. */
export function unavailableReason(m: Pick<MetricFacts, "coverage" | "personal">, scope: Scope, level: Level, hasBudget: boolean): string {
  if (m.personal && !hasBudget) return "Add your income in My budget below to see this";
  if (scope === "us" && m.coverage !== "us") return `${COVERAGE[m.coverage].only}: switch the map to ${DEEP_STATE.name} to see it`;
  return `Not available at ${LEVEL_LABEL[level]} level`;
}

/** The sidebar's categories in a scope: the U.S. view leaves out one that is all regional data (Crime, housing law). */
export function scopeCategories(cats: readonly string[], metrics: ReadonlyArray<Pick<Metric, "category" | "coverage">>, scope: Scope): string[] {
  return cats.filter((c) => scope === "ca" || metrics.some((m) => m.category === c && m.coverage === "us"));
}

/** Where a data point goes when it has no data at the map's level: the first of these that has it (in the scope). */
export const LEVEL_FALLBACK: readonly Level[] = ["county", "metro", "state", "zip", "city", "us"];

/** The level to show a data point at: the one asked for when it has data there, else the first fallback that does. */
export function levelFor(m: Pick<MetricFacts, "levels" | "available">, level: Level, scope: Scope): Level {
  if (hasData(m, level)) return level;
  return LEVEL_FALLBACK.find((l) => levelInScope(scope, l) && hasData(m, l)) ?? level;
}

// ---------- transitions: each returns the next state, which web/main.ts applies (go) ----------

/** A new level: the month goes back to the latest (each level has its own months). */
export const withLevel = (s: State, level: Level): State => (level === s.level ? s : { ...s, level, period: null });

/**
 * A new data point: a picked month is kept only for monthly data, and a question that doesn't hold the data point is
 * left (keepQuestion: picked from the question's own chips).
 */
export function withMetric(s: State, id: string, f: Facts, c: Catalog, keepQuestion = false): State {
  const next: State = { ...s, metric: id };
  if (!keepQuestion && next.q && !(f.questionMetrics(next.q) ?? []).includes(id)) next.q = null;
  if (next.period && c.metric(id)?.cadence !== "monthly") next.period = null;
  return next;
}

/**
 * Open an area (search, a click, a pin, the panel's links): at its own level when `level` (default; the national map
 * only in the U.S. view), and in its scope: an area outside the deep state belongs to the U.S. view, one at a level the
 * U.S. view hasn't (a city) to the deep view. `map`: back to the map from another view.
 */
export function withArea(s: State, id: string, f: Facts, o: { level?: boolean; map?: boolean } = {}): State {
  let next: State = { ...s, geo: id };
  const lvl = levelOf(id);
  if (next.scope === "ca" && inDeepState(id) === false) next = switchScope(next, "us", f);
  else if (next.scope === "us" && !levelInScope("us", lvl) && inDeepState(id)) next = switchScope(next, "ca", f);
  if (o.level !== false && levelInScope(next.scope, lvl)) next = withLevel(next, lvl);
  if (o.map) next = { ...next, view: "map" };
  return next;
}

/**
 * Open a guided question (its sidebar button, the welcome card): in California when it only works there, on its first
 * data point that has data at the map's level (else its first).
 */
export function withQuestion(s: State, q: { id: string; metrics: string[]; coverage?: Coverage }, f: Facts, c: Catalog): State {
  const next: State = { ...((q.coverage ?? "us") !== "us" ? switchScope(s, "ca", f) : s), q: q.id };
  const shows = (id: string) => {
    const m = c.metric(id);
    return !!m && hasData({ levels: m.levels, available: shownAvailability(m, m.available, next.scope, c.hasBudget) }, next.level);
  };
  return withMetric(next, q.metrics.find(shows) ?? q.metrics[0]!, f, c, true);
}

/**
 * Make a state one the app can show: a data point that isn't served (an old link) or needs a budget that isn't set
 * becomes Home Value, a question that doesn't hold the data point is left, and a level without the data point's data
 * moves to one that has it. Every transition passes through it (web/main.ts go).
 */
export function settle(s: State, f: Facts, c: Catalog): State {
  let next = s;
  let m = c.metric(next.metric);
  if (!m || (m.personal && !c.hasBudget)) {
    next = { ...next, metric: DEFAULT_METRIC, period: null };
    m = c.metric(DEFAULT_METRIC);
  }
  if (next.q && !(f.questionMetrics(next.q) ?? []).includes(next.metric)) next = { ...next, q: null };
  if (m) next = withLevel(next, levelFor({ levels: m.levels, available: shownAvailability(m, m.available, next.scope, c.hasBudget) }, next.level, next.scope));
  return next;
}
